import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MzicSvgComponent } from '../mzic-svg/mzic-svg.component';
import { MzicToastService } from '../mzic-toast/services/mzic-toast.service';

@Component({
  selector: 'mzic-badge',
  imports: [CommonModule, MzicSvgComponent],
  templateUrl: './mzic-badge.component.html',
  styleUrls: ['./mzic-badge.component.scss'],
})
export class MzicBadgeComponent {
  @Input() text = '';
  @Input() formattedText = '';
  @Input() showCopy = false;
  @Input() copyTitle = 'Copy';
  @Input() enableCopyToClipboard = true;
  @Input() copySuccessMessage = '';
  @Input() iconSrc = '';
  @Input() theme: 'light' | 'dark' = 'light';

  @Output() copyClicked = new EventEmitter<string>();

  private mzicToastService = inject(MzicToastService, { optional: true });
  private translocoService = inject(TranslocoService, { optional: true });

  get displayText(): string {
    return this.formattedText || this.text;
  }

  onCopyClick(): void {
    if (this.showCopy) {
      if (this.enableCopyToClipboard) {
        this.copyToClipboard(this.text);
      }
      this.copyClicked.emit(this.text);
    }
  }

  private copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.showCopySuccessToast();
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'absolute';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.showCopySuccessToast();
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
      document.body.removeChild(textArea);
    }
  }

  private showCopySuccessToast(): void {
    if (!this.mzicToastService) return;

    const message =
      this.copySuccessMessage ||
      (this.translocoService
        ? this.translocoService.translate('copySuccess')
        : 'Copied to clipboard');

    this.mzicToastService.create({
      title: message,
      icon: 'assets/icons/mzic-check-mark-filled.svg',
      iconColor: 'green-500',
      time: 3000,
    });
  }
}
